import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import apiService from '../../services/api';

const RegisterPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    role: 'student',
    schoolId: '',
    classId: '' // 🟢 [修改] 字段名改为 classId 以匹配逻辑
  });
  
  const [schools, setSchools] = useState([]);
  const [classes, setClasses] = useState([]); // 🟢 [新增] 班级列表
  const [loadingSchools, setLoadingSchools] = useState(false);
  const [loadingClasses, setLoadingClasses] = useState(false); // 🟢 [新增] 班级加载状态

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  // 1. 加载学校列表 (保持不变)
  useEffect(() => {
    const fetchSchools = async () => {
      setLoadingSchools(true);
      try {
        const res = await apiService.client.get('/schools');
        let list = [];
        if (Array.isArray(res)) list = res;
        else if (res.data && Array.isArray(res.data)) list = res.data;
        else if (res.schools) list = res.schools;
        setSchools(list);
      } catch (err) {
        console.error("Failed to load schools", err);
        setSchools([]); 
      } finally {
        setLoadingSchools(false);
      }
    };
    fetchSchools();
  }, []);

  // 🟢 [新增] 2. 监听 SchoolId 和 Role 变化，级联加载班级
  useEffect(() => {
    const fetchClasses = async () => {
      // 只有当角色是学生且已经选择了学校时，才去加载班级
      if (formData.role !== 'student' || !formData.schoolId) {
        setClasses([]);
        return;
      }

      setLoadingClasses(true);
      try {
        // 调用后端刚才写的新接口
        const res = await apiService.client.get('/classes/public', {
          params: { schoolId: formData.schoolId }
        });

        // 兼容解包逻辑
        let list = [];
        if (Array.isArray(res)) list = res;
        else if (res.data && Array.isArray(res.data)) list = res.data;
        
        setClasses(list);
      } catch (err) {
        console.error("Failed to load classes", err);
        setClasses([]);
      } finally {
        setLoadingClasses(false);
      }
    };

    fetchClasses();
  }, [formData.schoolId, formData.role]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // 🟢 [修改] 如果切换角色，重置班级选择
    if (name === 'role') {
        setFormData(prev => ({
            ...prev,
            role: value,
            schoolId: '',
            classId: '' // 切换角色时清空班级，避免逻辑混淆
        }));
    } else if (name === 'schoolId') {
        // 🟢 [建议] 如果切换学校，也要清空班级
        setFormData(prev => ({
            ...prev,
            schoolId: value,
            classId: '' 
        }));
    } else {
        setFormData({
          ...formData,
          [name]: value
        });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    // Validation
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match'); setLoading(false); return;
    }
    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters long'); setLoading(false); return;
    }

    // School and class are now optional (social media user acquisition)

    try {
      // 🟢 [修改] 捕获注册返回的结果
      const res = await register(formData);
      //console.log("👉 注册接口返回的完整数据:", res);
      
      // 🟢 [新增] 检查返回的用户状态 (兼容后端直接返回 user 对象或嵌套在 data 中)
      // 假设后端自动审批后，status 已经是 'approved'
      const isApproved = res?.status === 'approved' || res?.user?.status === 'approved';

      if (isApproved) {
         // ✅ 情况 A: 自动通过
         setSuccess('Registration successful! Redirecting to login...');
         setTimeout(() => {
            navigate('/login');
         }, 1500);
      } else {
         // ⏳ 情况 B: 仍需审核 (保留原有逻辑)
         setSuccess('Registration successful! Your account is pending approval from an administrator.');
         setTimeout(() => {
            navigate('/login');
         }, 3000);
      }

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-sky-100 via-sky-200 to-blue-300 p-4">
      <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.5 }} className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md">
        <div className="text-center mb-8">
          <motion.h1 initial={{ y: -20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.2 }} className="text-3xl font-bold bg-gradient-to-r from-sky-500 to-blue-500 bg-clip-text text-transparent font-kid">
            VOCAB RUN
          </motion.h1>
          <p className="text-gray-600 mt-2">Join the adventure!</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg text-sm">{error}</motion.div>}
          {success && <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-lg text-sm">{success}</motion.div>}

          <motion.div initial={{ x: -20, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ delay: 0.3 }}>
            <label className="block text-gray-700 text-sm font-bold mb-2">Full Name</label>
            <input type="text" name="name" value={formData.name} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder="Enter your full name" required />
          </motion.div>

          <motion.div initial={{ x: -20, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ delay: 0.4 }}>
            <label className="block text-gray-700 text-sm font-bold mb-2">Email Address</label>
            <input type="email" name="email" value={formData.email} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder="Enter your email" required />
          </motion.div>

          <motion.div initial={{ x: -20, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ delay: 0.5 }}>
            <label className="block text-gray-700 text-sm font-bold mb-2">Role</label>
            <select name="role" value={formData.role} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" required>
              <option value="student">Student</option>
              <option value="teacher">Teacher</option>
            </select>
          </motion.div>

          <motion.div initial={{ x: -20, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ delay: 0.6 }}>
            <label className="block text-gray-700 text-sm font-bold mb-2">School <span className="text-gray-400 text-xs font-normal">(optional)</span></label>
            <select 
              name="schoolId" 
              value={formData.schoolId} 
              onChange={handleChange} 
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all disabled:bg-gray-100"
              disabled={loadingSchools || loadingClasses}
            >
              <option value="">{loadingSchools ? 'Loading schools...' : 'Select your school (optional)'}</option>
              {schools.map(school => (
                <option key={school.id} value={school.id}>{school.name}</option>
              ))}
            </select>
            {schools.length === 0 && !loadingSchools && <p className="text-xs text-orange-500 mt-1">No schools found.</p>}
          </motion.div>

          {/* 🟢 [修改] 班级选择 - 仅当角色是学生且选择了学校时显示 */}
          {formData.role === 'student' && formData.schoolId && (
            <motion.div initial={{ x: -20, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ delay: 0.65 }}>
              <label className="block text-gray-700 text-sm font-bold mb-2">Class <span className="text-gray-400 text-xs font-normal">(optional)</span></label>
              <select
                name="classId"
                value={formData.classId}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all disabled:bg-gray-100"
                disabled={loadingClasses}
              >
                <option value="">{loadingClasses ? 'Loading classes...' : 'Select your class (optional)'}</option>
                {classes.length > 0 ? (
                    classes.map(cls => (
                        <option key={cls.id} value={cls.id}>{cls.name} ({cls.academicYear})</option>
                    ))
                ) : (
                    !loadingClasses && <option value="" disabled>No classes found for this school</option>
                )}
              </select>
              {classes.length === 0 && !loadingClasses && (
                 <p className="text-xs text-orange-500 mt-1">
                   No classes available. Please ask your teacher to create one.
                 </p>
              )}
            </motion.div>
          )}

          <motion.div initial={{ x: -20, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ delay: 0.7 }}>
            <label className="block text-gray-700 text-sm font-bold mb-2">Password</label>
            <input type="password" name="password" value={formData.password} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder="Enter your password" required />
          </motion.div>

          <motion.div initial={{ x: -20, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ delay: 0.8 }}>
            <label className="block text-gray-700 text-sm font-bold mb-2">Confirm Password</label>
            <input type="password" name="confirmPassword" value={formData.confirmPassword} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder="Confirm your password" required />
          </motion.div>

          <motion.button initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.9 }} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} type="submit" disabled={loading || loadingSchools || loadingClasses} className="w-full bg-gradient-to-r from-sky-300 to-blue-400 hover:from-sky-400 hover:to-blue-500 text-white font-bold py-3 px-6 rounded-lg shadow-lg transform transition-all duration-200 disabled:opacity-50">
            {loading ? <div className="flex items-center justify-center"><div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>Creating Account...</div> : 'Create Account'}
          </motion.button>
        </form>

        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.0 }} className="text-center mt-6">
          <p className="text-gray-600 text-sm">Already have an account? <Link to="/login" className="text-sky-500 hover:text-sky-700 font-semibold transition-colors">Login here</Link></p>
        </motion.div>

        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.1 }} className="text-center mt-4">
          <Link to="/" className="text-gray-500 hover:text-gray-700 text-sm transition-colors">← Back to Home</Link>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default RegisterPage;